import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import OrgUnitAdminUserConstants from '../OrgUnitAdminUserConstants';
import NavLinks from '../../../meet/NavLinks';
import EntryNavLinks from '../../EntryNavLinks';
import MyMeetsNavLinks from '../../../myMeets/MyMeetsNavLinks';

import validate from './OrgUnitAdminMeetInfoValidation';

import useOmeMeetData from '../../../../state/omeMeet/UseOmeMeetData';
import useOmeMeetOrgUnitEntryInfoData from '../../../../state/omeMeetOrgUnitEntryInfo/UseOmeMeetOrgUnitEntryInfoData';
import useOmeMeetOrgUnitEntryData from '../../../../state/omeMeetOrgUnitEntry/UseOmeMeetOrgUnitEntryData';
import useOmeMeetOrgUnitAthleteEntryData from '../../../../state/omeMeetOrgUnitAthleteEntry/UseOmeMeetOrgUnitAthleteEntryData';
import useOmeMeetOrgUnitAthleteEntryRelayRosterEntryData from '../../../../state/omeMeetOrgUnitAthleteEntryRelayRosterEntry/UseOmeMeetOrgUnitAthleteEntryRelayRosterEntryData';
import useOmeMeetOrgUnitNonAthleteEntryData from '../../../../state/omeMeetOrgUnitNonAthleteEntry/UseOmeMeetOrgUnitNonAthleteEntryData';
import useOmeMeetOrgUnitContactData from '../../../../state/omeMeetOrgUnitContact/UseOmeMeetOrgUnitContactData';
import useOmeMeetOrgUnitEntryRosterPermissionsData from '../../../../state/omeMeetOrgUnitEntryRosterPermissions/UseOmeMeetOrgUnitEntryRosterPermissionsData';
import useOUAdminEntryContextData from '../../../../state/ouAdminEntryContextView/UseOUAdminEntryContextData';

import useDateTimeData from '@usas/ui-web/state/datetime/UseDateTimeData';

import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';
import useSearchMeetData from '@usas/ui-web/state/searchMeet/UseSearchMeetData';
import useSelectOrgUnitData from '@usas/ui-web/state/selectOrgUnit/UseSelectOrgUnitData';
import useMyMeetsFiltersData from '../../../../state/myMeetsFilters/UseMyMeetsFiltersData';

import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import { formatDateTime } from '@usas/ui-web/utils/DateFunctions';

const INITIAL_VIEW_STATE = {
  initialLoadComplete: false,
  submitButton: '',
  contact: {},
  tryRedirect: false
};

const INITIAL_MODAL_STATE = {
  displayPopUp: false,
  modalTitle: 'Delete Entry Contact?',
  contactName: '',
  emailAddress: '',
  omeMeetOrgUnitContactId: Constants.DEFAULT_ID
};

const useOrgUnitAdminMeetInfo = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { omeMeetState } = useOmeMeetData();
  const { omeMeetOrgUnitEntryInfoState, getOmeMeetOrgUnitEntryInfo } = useOmeMeetOrgUnitEntryInfoData();
  const { omeMeetOrgUnitEntryState, getOmeMeetOrgUnitEntryById } = useOmeMeetOrgUnitEntryData();
  const { omeMeetOrgUnitAthleteEntryState, getOrgUnitAthleteEntryRoster } = useOmeMeetOrgUnitAthleteEntryData();
  const { omeMeetOrgUnitAthleteEntryRelayRosterEntryState, getOrgUnitAthleteEntryRelayRosterEntry } = useOmeMeetOrgUnitAthleteEntryRelayRosterEntryData();
  const { omeMeetOrgUnitNonAthleteEntryState, getOrgUnitNonAthleteEntryCredentials } = useOmeMeetOrgUnitNonAthleteEntryData();
  const { omeMeetOrgUnitContactState, getOmeMeetOrgUnitContacts, deleteOmeMeetOrgUnitContact } = useOmeMeetOrgUnitContactData();
  const { omeMeetOrgUnitEntryRosterPermissionsState, getOmeMeetOrgUnitEntryRosterPermissions, putOmeMeetOrgUnitEntryRosterPermissions } = useOmeMeetOrgUnitEntryRosterPermissionsData();
  const { dateTimeState } = useDateTimeData();
  const { oUAdminEntryContextState, setOUAdminEntryContextState } = useOUAdminEntryContextData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();
  const { searchMeetState, setSearchMeetState } = useSearchMeetData();
  const { selectOrgUnitState } = useSelectOrgUnitData();
  const { myMeetsFiltersState } = useMyMeetsFiltersData();
  const { errorState, formState, isSubmitting, handleSubmit, updateFormState, setFormData, setErrors } = useForm(getInitialFormState, submitFormCallback, validate);
  const [state, setState] = useState(INITIAL_VIEW_STATE);
  const [tryGetContactsState, setTryGetContactsState] = useState(false);
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);

  const tryValidateBeforeRedirect = async (submitButton, contact) => {
    const errors = await validate(formState) || {};
    setErrors(errors);
    if (Object.keys(errors).length === 0) {
      setState({
        ...state, submitButton: submitButton, contact: contact ? contact : {}, tryRedirect: true
      });
    }
    else {
      setState({
        ...state, submitButton: '', contact: {}, tryRedirect: false
      });
    }
  };

  const tryValidateBeforeShowingDeletePopUp = async (contact) => {
    const errors = await validate(formState) || {};
    setErrors(errors);
    if (Object.keys(errors).length === 0) {
      setModalState({
        ...modalState,
        displayPopUp: true,
        contactName: (contact.firstName || contact.middleName || contact.lastName) ? `${contact.firstName || ''} ${contact.preferredName !== '' && contact.preferredName !== contact.firstName ? '"' + contact.preferredName + '"' : ''} ${contact.middleName || ''} ${contact.lastName || ''}` : '',
        emailAddress: contact.emailAddress || '',
        omeMeetOrgUnitContactId: contact.omeMeetOrgUnitContactId || Constants.DEFAULT_ID
      });
    }
  };

  const onNextButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    const isSaved = formStateHasBeenSaved(formState);
    if (isSaved === true) {
      tryValidateBeforeRedirect(OrgUnitAdminUserConstants.CONTINUE);
    }
    else {
      handleSubmit();
      setState({ ...state, submitButton: OrgUnitAdminUserConstants.CONTINUE, tryRedirect: true });
    }
  }

  const onSaveAndExitButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    const isSaved = formStateHasBeenSaved(formState);
    if (isSaved === true) {
      tryValidateBeforeRedirect(OrgUnitAdminUserConstants.SAVE_AND_EXIT);
    }
    else {
      handleSubmit();
      setState({ ...state, submitButton: OrgUnitAdminUserConstants.SAVE_AND_EXIT, tryRedirect: true });
    }
  };

  const onAddContact = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    tryValidateBeforeRedirect(OrgUnitAdminUserConstants.ADD);
  };

  const onEditContact = (e, contact) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    tryValidateBeforeRedirect(OrgUnitAdminUserConstants.EDIT, contact);
  };

  const onDeleteContactClicked = (e, contact) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    tryValidateBeforeShowingDeletePopUp(contact);
  };

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setModalState(INITIAL_MODAL_STATE);
  };

  const onDeleteContact = (e, omeMeetOrgUnitContactId) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    const isSaved = formStateHasBeenSaved(formState);
    if (isSaved === false) {
      handleSubmit();
    }

    deleteOmeMeetOrgUnitContact(omeMeetOrgUnitContactId);
    onModalCanceled();
    setTryGetContactsState(true);
  }

  useEffect(() => {
    if (Object.keys(omeMeetState.objData).length > 0 && (Object.keys(omeMeetOrgUnitEntryState.objData).length > 0) &&
      Object.keys(omeMeetOrgUnitEntryRosterPermissionsState.objData).length > 0 && dateTimeState.isObjLoaded === true) {
      const isSaved = formStateHasBeenSaved(formState);
      setLeftNavModalState({ ...leftNavModalState, formStateSaved: isSaved });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState, omeMeetState.objData, omeMeetOrgUnitEntryState.objData, omeMeetOrgUnitEntryRosterPermissionsState.objData, omeMeetOrgUnitContactState.arrayData, dateTimeState.isObjLoaded]);

  useEffect(() => {
    setOUAdminEntryContextState({ ...oUAdminEntryContextState, showNav: true });
    //Get updated info after a purchase has been made
    if (location.state && location.state.tryGet === true) {
      getOmeMeetOrgUnitEntryInfo(omeMeetOrgUnitEntryState.objData.omeMeetOrgUnitEntryId);
      getOmeMeetOrgUnitEntryById(omeMeetState.objData.omeMeetId, omeMeetOrgUnitEntryState.objData.omeMeetEntryId, omeMeetOrgUnitEntryState.objData.omeMeetOrgUnitEntryId);
      getOrgUnitAthleteEntryRoster(omeMeetOrgUnitEntryState?.objData.omeMeetOrgUnitEntryId);
      //Get updated relay entries too
      getOrgUnitAthleteEntryRelayRosterEntry(omeMeetOrgUnitEntryState.objData.omeMeetOrgUnitEntryId);
      //Get updated coaches too
      getOrgUnitNonAthleteEntryCredentials(omeMeetOrgUnitEntryState?.objData.omeMeetOrgUnitEntryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (omeMeetOrgUnitEntryState.isObjLoaded === true && omeMeetOrgUnitEntryState?.objData?.omeMeetOrgUnitEntryId) {
      if (omeMeetOrgUnitAthleteEntryState.isArrayLoaded === false && omeMeetOrgUnitAthleteEntryState.isArrayLoading === false) {
        getOrgUnitAthleteEntryRoster(omeMeetOrgUnitEntryState?.objData.omeMeetOrgUnitEntryId);
      }
      if (omeMeetOrgUnitAthleteEntryRelayRosterEntryState.isArrayLoaded === false && omeMeetOrgUnitAthleteEntryRelayRosterEntryState.isArrayLoading === false) {
        getOrgUnitAthleteEntryRelayRosterEntry(omeMeetOrgUnitEntryState?.objData.omeMeetOrgUnitEntryId);
      }
      if (omeMeetOrgUnitNonAthleteEntryState.isArrayLoaded === false && omeMeetOrgUnitNonAthleteEntryState.isArrayLoading === false) {
        getOrgUnitNonAthleteEntryCredentials(omeMeetOrgUnitEntryState?.objData.omeMeetOrgUnitEntryId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitEntryState, omeMeetOrgUnitAthleteEntryState, omeMeetOrgUnitAthleteEntryRelayRosterEntryState, omeMeetOrgUnitNonAthleteEntryState])

  useEffect(() => {
    if (location.state && location.state?.tryGetContacts === true) {
      setTryGetContactsState(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (omeMeetOrgUnitEntryInfoState.isObjLoaded === true && omeMeetOrgUnitEntryInfoState?.objData?.omeMeetId &&
      omeMeetOrgUnitEntryState.isObjLoaded === true && omeMeetOrgUnitEntryState?.objData?.orgUnitId && omeMeetOrgUnitEntryState?.objData?.omeMeetOrgUnitEntryId
      && omeMeetOrgUnitContactState.isArrayLoading !== true && omeMeetOrgUnitContactState.isSaving !== true
      && (omeMeetOrgUnitContactState.isArrayLoaded !== true || tryGetContactsState === true)) {
      getOmeMeetOrgUnitEntryInfo(omeMeetOrgUnitEntryState.objData.omeMeetOrgUnitEntryId);
      getOmeMeetOrgUnitContacts(omeMeetOrgUnitEntryInfoState.objData?.omeMeetId, omeMeetOrgUnitEntryState.objData?.orgUnitId);
      if (tryGetContactsState === true) {
        setTryGetContactsState(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitEntryInfoState, omeMeetOrgUnitEntryState, omeMeetOrgUnitContactState, tryGetContactsState])

  useEffect(() => {
    if (omeMeetOrgUnitEntryRosterPermissionsState.isObjLoaded === false || (omeMeetOrgUnitEntryRosterPermissionsState.isObjLoaded === true &&
      omeMeetOrgUnitEntryRosterPermissionsState.omeMeetOrgUnitEntryId !== omeMeetOrgUnitEntryState.objData?.omeMeetOrgUnitEntryId)) {
      getOmeMeetOrgUnitEntryRosterPermissions(omeMeetOrgUnitEntryState.objData?.omeMeetOrgUnitEntryId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitEntryState, omeMeetOrgUnitEntryRosterPermissionsState])

  useEffect(() => {
    if (omeMeetOrgUnitEntryRosterPermissionsState.isObjLoaded === true && omeMeetOrgUnitEntryRosterPermissionsState.omeMeetOrgUnitEntryId === omeMeetOrgUnitEntryState.objData?.omeMeetOrgUnitEntryId &&
      omeMeetOrgUnitEntryState.isObjLoaded === true && omeMeetOrgUnitEntryInfoState.isObjLoaded === true && dateTimeState.isObjLoaded === true) {
      //Set formState from location.state if 'Back' button was clicked on contact detail page
      if (location.state && location.state?.meetInfoFormState) {
        setFormData({
          ...formState,
          orgUnitAthleteRegistrationAllowed: location.state?.meetInfoFormState?.orgUnitAthleteRegistrationAllowed ?? '',
          orgUnitRegistrationStartDate: location.state?.meetInfoFormState?.orgUnitRegistrationStartDate || Constants.BLANK_DATETIME_STRING,
          orgUnitRegistrationEndDate: location.state?.meetInfoFormState?.orgUnitRegistrationEndDate || Constants.BLANK_DATETIME_STRING,
          timeZone: omeMeetOrgUnitEntryInfoState.objData?.timeZone || '',
          meetEntryStartDate: omeMeetOrgUnitEntryInfoState.objData?.meetEntryStartDate ? formatDateTime(omeMeetOrgUnitEntryInfoState.objData?.meetEntryStartDate) : Constants.BLANK_DATETIME_STRING,
          meetEntryStartDateDatabaseFormat: omeMeetOrgUnitEntryInfoState.objData?.meetEntryStartDate || '',
          meetEntryEndDate: omeMeetOrgUnitEntryInfoState.objData?.meetEntryEndDate ? formatDateTime(omeMeetOrgUnitEntryInfoState.objData?.meetEntryEndDate) : Constants.BLANK_DATETIME_STRING,
          meetEntryEndDateDatabaseFormat: omeMeetOrgUnitEntryInfoState.objData?.meetEntryEndDate || ''
        });
      }
      else {
        //Set formState from omeMeetOrgUnitEntryRosterPermissionsState
        setFormData({
          ...formState,
          orgUnitAthleteRegistrationAllowed: omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitAthleteRegistrationAllowed ?? '',
          orgUnitRegistrationStartDate: omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationStartDate ? formatDateTime(omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationStartDate) : Constants.BLANK_DATETIME_STRING,
          orgUnitRegistrationEndDate: omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationEndDate ? formatDateTime(omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationEndDate) : Constants.BLANK_DATETIME_STRING,
          timeZone: omeMeetOrgUnitEntryInfoState.objData?.timeZone || '',
          meetEntryStartDate: omeMeetOrgUnitEntryInfoState.objData?.meetEntryStartDate ? formatDateTime(omeMeetOrgUnitEntryInfoState.objData?.meetEntryStartDate) : Constants.BLANK_DATETIME_STRING,
          meetEntryStartDateDatabaseFormat: omeMeetOrgUnitEntryInfoState.objData?.meetEntryStartDate || '',
          meetEntryEndDate: omeMeetOrgUnitEntryInfoState.objData?.meetEntryEndDate ? formatDateTime(omeMeetOrgUnitEntryInfoState.objData?.meetEntryEndDate) : Constants.BLANK_DATETIME_STRING,
          meetEntryEndDateDatabaseFormat: omeMeetOrgUnitEntryInfoState.objData?.meetEntryEndDate || ''
        });
      }
      setState({ ...state, initialLoadComplete: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitEntryRosterPermissionsState, omeMeetOrgUnitEntryState, omeMeetOrgUnitEntryInfoState, dateTimeState.isObjLoaded])

  useEffect(() => {
    //Handle date time picker loading issue
    if (state.initialLoadComplete === true && omeMeetOrgUnitEntryRosterPermissionsState.isObjLoaded === true &&
      omeMeetOrgUnitEntryRosterPermissionsState.omeMeetOrgUnitEntryId === omeMeetOrgUnitEntryState.objData?.omeMeetOrgUnitEntryId &&
      omeMeetOrgUnitEntryState.isObjLoaded === true && omeMeetOrgUnitEntryInfoState.isObjLoaded === true && dateTimeState.isObjLoaded === true) {
      //Set formState from location.state if 'Back' button was clicked on contact detail page
      if (location.state && location.state?.meetInfoFormState) {
        setFormData({
          ...formState,
          orgUnitAthleteRegistrationAllowed: location.state?.meetInfoFormState?.orgUnitAthleteRegistrationAllowed ?? '',
          orgUnitRegistrationStartDate: location.state?.meetInfoFormState?.orgUnitRegistrationStartDate || Constants.BLANK_DATETIME_STRING,
          orgUnitRegistrationEndDate: location.state?.meetInfoFormState?.orgUnitRegistrationEndDate || Constants.BLANK_DATETIME_STRING
        })
      }
      else {
        //Set formState from omeMeetOrgUnitEntryRosterPermissionsState
        setFormData({
          ...formState,
          orgUnitAthleteRegistrationAllowed: omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitAthleteRegistrationAllowed ?? '',
          orgUnitRegistrationStartDate: omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationStartDate ? formatDateTime(omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationStartDate) : Constants.BLANK_DATETIME_STRING,
          orgUnitRegistrationEndDate: omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationEndDate ? formatDateTime(omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationEndDate) : Constants.BLANK_DATETIME_STRING
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.initialLoadComplete, omeMeetOrgUnitEntryRosterPermissionsState, omeMeetOrgUnitEntryState, omeMeetOrgUnitEntryInfoState, dateTimeState.isObjLoaded])

  useEffect(() => {
    if (omeMeetOrgUnitEntryRosterPermissionsState.isSaving === false && isSubmitting === false && state.tryRedirect === true && Object.keys(errorState)?.length === 0) {
      if (state.submitButton === OrgUnitAdminUserConstants.CONTINUE) {
        if (omeMeetOrgUnitContactState.arrayData.length === 0) {
          setErrors({ ...errorState, error: 'At least one Entry Contact is required.' });
        }
        else {
          setLeftNavModalState({ ...leftNavModalState, formStateSaved: true });
          if (omeMeetState.route !== '') {
            navigate(NavLinks.OU_ADMIN_ROSTER_ENTRIES);
          }
          else {
            navigate(EntryNavLinks.OU_ADMIN_ROSTER_ENTRIES);
          }
        }
      }
      else if (state.submitButton === OrgUnitAdminUserConstants.SAVE_AND_EXIT) {
        if (omeMeetOrgUnitContactState.arrayData.length === 0) {
          setErrors({ ...errorState, error: 'At least one Entry Contact is required.' });
        }
        else {
          if (omeMeetState.route !== '') {
            setSearchMeetState({ ...searchMeetState, showMeetHeader: true });
            navigate(NavLinks.MEET_ENTRY_TEAM, { state: { tryGetOmeMeet: true } });
          }
          else {
            navigate(MyMeetsNavLinks.MEET_ENTRY_TEAM, { state: { selectOrgUnitState: selectOrgUnitState, myMeetsFiltersState: myMeetsFiltersState } });
          }
        }
      }
      else if (state.submitButton === OrgUnitAdminUserConstants.ADD) {
        if (omeMeetState.route === '') {
          //OME Route
          navigate(EntryNavLinks.OU_ADMIN_MEET_INFO_CONTACTS_DETAIL, { state: { meetInfoFormState: formState } });
        }
        else {
          //Meet Route
          navigate(NavLinks.OU_ADMIN_MEET_INFO_CONTACTS_DETAIL, { state: { meetInfoFormState: formState } });
        }
      }
      else if (state.submitButton === OrgUnitAdminUserConstants.EDIT) {
        if (omeMeetState.route === '') {
          //OME Route
          navigate(EntryNavLinks.OU_ADMIN_MEET_INFO_CONTACTS_DETAIL, {
            state: {
              omeMeetOrgUnitContactId: state.contact?.omeMeetOrgUnitContactId || '',
              memberId: state.contact?.memberId || '',
              personId: state.contact?.personId || '',
              emailAddress: state.contact?.emailAddress || '',
              meetInfoFormState: formState
            }
          });
        }
        else {
          //Meet Route
          navigate(NavLinks.OU_ADMIN_MEET_INFO_CONTACTS_DETAIL, {
            state: {
              omeMeetOrgUnitContactId: state.contact?.omeMeetOrgUnitContactId || '',
              memberId: state.contact?.memberId || '',
              personId: state.contact?.personId || '',
              emailAddress: state.contact?.emailAddress || '',
              meetInfoFormState: formState
            }
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, omeMeetOrgUnitEntryRosterPermissionsState.isSaving, state, omeMeetOrgUnitContactState, errorState]);

  function submitFormCallback(formState) {
    if (formStateHasBeenSaved(formState) === false) {
      putOmeMeetOrgUnitEntryRosterPermissions(omeMeetOrgUnitEntryState?.objData?.omeMeetOrgUnitEntryId, updateOmeMeetOrgUnitEntryRosterPermissionsObject(formState));
    }
  }

  function updateOmeMeetOrgUnitEntryRosterPermissionsObject(formState) {
    const omeMeetOrgUnitEntryRosterPermissionsCopy = JSON.parse(JSON.stringify(omeMeetOrgUnitEntryRosterPermissionsState.objData));
    return {
      ...omeMeetOrgUnitEntryRosterPermissionsCopy,
      orgUnitAthleteRegistrationAllowed: formState.orgUnitAthleteRegistrationAllowed,
      orgUnitRegistrationStartDate: formState.orgUnitAthleteRegistrationAllowed === true ? formState.orgUnitRegistrationStartDate : null,
      orgUnitRegistrationEndDate: formState.orgUnitAthleteRegistrationAllowed === true ? formState.orgUnitRegistrationEndDate : null
    }
  }

  function formStateHasBeenSaved(formState) {
    //compare formState to omeMeetOrgUnitEntryRosterPermissionsState to see if formState has been saved
    if (formState?.orgUnitAthleteRegistrationAllowed !== omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitAthleteRegistrationAllowed
      && (formState?.orgUnitAthleteRegistrationAllowed !== '' || omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitAthleteRegistrationAllowed !== null)) {
      return false;
    }
    if (formState?.orgUnitAthleteRegistrationAllowed === true &&
      formState?.orgUnitRegistrationStartDate !== formatDateTime(omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationStartDate)
      && (formState?.orgUnitRegistrationStartDate !== Constants.BLANK_DATETIME_STRING || omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationStartDate !== null)) {
      return false;
    }
    if (formState?.orgUnitAthleteRegistrationAllowed === true &&
      formState?.orgUnitRegistrationEndDate !== formatDateTime(omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationEndDate)
      && (formState?.orgUnitRegistrationEndDate !== Constants.BLANK_DATETIME_STRING || omeMeetOrgUnitEntryRosterPermissionsState.objData?.orgUnitRegistrationEndDate !== null)) {
      return false;
    }

    return true;
  }

  function getInitialFormState() {
    return {
      orgUnitAthleteRegistrationAllowed: '',
      orgUnitRegistrationStartDate: Constants.BLANK_DATETIME_STRING,
      orgUnitRegistrationEndDate: Constants.BLANK_DATETIME_STRING,
      timeZone: '',
      meetEntryStartDate: Constants.BLANK_DATETIME_STRING,
      meetEntryStartDateDatabaseFormat: '',
      meetEntryEndDate: Constants.BLANK_DATETIME_STRING,
      meetEntryEndDateDatabaseFormat: ''
    }
  }

  return {
    state,
    modalState,
    oUAdminEntryContextState,
    omeMeetOrgUnitEntryState,
    omeMeetOrgUnitEntryInfoState,
    omeMeetOrgUnitAthleteEntryState,
    omeMeetOrgUnitAthleteEntryRelayRosterEntryState,
    omeMeetOrgUnitNonAthleteEntryState,
    omeMeetOrgUnitContactState,
    omeMeetOrgUnitEntryRosterPermissionsState,
    dateTimeState,
    errorState,
    formState,
    onFormValueChanged: updateFormState,
    handleSubmit,
    onNextButtonClicked,
    onSaveAndExitButtonClicked,
    formStateHasBeenSaved,
    updateOmeMeetOrgUnitEntryRosterPermissionsObject,
    onAddContact,
    onEditContact,
    onDeleteContactClicked,
    onDeleteContact,
    onModalCanceled
  };
}

export default useOrgUnitAdminMeetInfo;